import React, { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './generic-dialogue.scss';

interface CloseIconProps {
  width?: number;
  height?: number;
  className?: string;
}

export const CloseIcon = ({
  width = 20,
  height = 20,
  className = '',
}: CloseIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
      />
    </svg>
  );
};

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: string;
  children: ReactNode;
  subheader?: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
  maxHeight?: string;
  minHeight?: string;
  avatar?: ReactNode;
  animate?: boolean;
}

export const GenericDialogue = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  subheader,
  footer,
  maxWidth = '135rem',
  maxHeight = '95vh',
  minHeight,
  avatar,
  animate = true,
}: DialogProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const noAnimateClass = animate ? '' : ' dialog-no-animate';

  const dialog = (
    <div className={`dialog-overlay${noAnimateClass}`}>
      <div className={`dialog-container${noAnimateClass}`} style={{ maxWidth }}>
        <div
          className="dialog-content"
          style={
            {
              '--dialog-max-height': maxHeight,
              '--dialog-min-height': minHeight,
            } as React.CSSProperties
          }
        >
          <div className="dialog-header">
            <div className="dialog-header-left">
              {avatar && <div className="dialog-avatar">{avatar}</div>}
              <div>
                <h2 className="dialog-title">{title}</h2>
                {description && (
                  <p className="dialog-description">{description}</p>
                )}
              </div>
            </div>
            <button
              className="dialog-close"
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
          {subheader && <div className="dialog-subheader">{subheader}</div>}
          <div className="modern-dialog-body">{children}</div>
          {footer && <div className="dialog-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
};

export default GenericDialogue;
